/**
 * 门户路由解析：菜单树 path 匹配 + templateId → 模板 key
 */
import { HOME_PATH } from '@/components/layout/utils/navMenu'
import {
  getMenuNodePath,
  isPathUnderMenu,
  normalizeContentPath,
  normalizeMenuPath,
  resolveMenuNodePath,
  slugFromMenuPath
} from '@/components/portal/common/utils/menuPath'
import {
  getDefaultTemplateIdForContentType,
  getTemplateEntry,
  resolveMenuTemplateId,
  resolveTemplateId,
  CONTENT_PAGE_TEMPLATE_ID
} from '@/components/portal/common/utils/menuTemplateRegistry'
import { isPortalMenuVisible } from '@/components/portal/common/utils/portalMenuVisibility'

const FOOTER_LINK_TYPE_TO_TEMPLATE = {
  CONTACT: {
    templateId: 91,
    templateKey: 'CONTACT_FORM',
    contentVariant: null,
    defaultName: '联系我们'
  },
  LEGAL: {
    templateId: CONTENT_PAGE_TEMPLATE_ID,
    templateKey: 'CONTENT_PAGE',
    contentVariant: 'legal',
    defaultName: '法律声明'
  },
  PRIVACY: {
    templateId: CONTENT_PAGE_TEMPLATE_ID,
    templateKey: 'CONTENT_PAGE',
    contentVariant: 'privacy',
    defaultName: '隐私保护'
  }
}

const FOOTER_LINK_TYPE_TO_UTILITY = {
  CONTACT: 'contact',
  LEGAL: 'legal',
  PRIVACY: 'privacy'
}

const FIXED_FOOTER_LINK_URLS = {
  LEGAL: '/legal',
  PRIVACY: '/privacy'
}

function resolveFooterLinkPath(link, menus = []) {
  const type = String(link?.type || '').trim().toUpperCase()
  const utilityType = FOOTER_LINK_TYPE_TO_UTILITY[type]
  if (!utilityType) return ''
  const menuPath = findMenuPathByUtilityType(menus, utilityType)
  if (!menuPath) return ''
  if (FIXED_FOOTER_LINK_URLS[type]) return FIXED_FOOTER_LINK_URLS[type]
  return normalizeMenuPath(menuPath)
}

function findFooterLinkByPath(path, footerLinks = [], menus = []) {
  const target = normalizeContentPath(path)
  if (!target || target === HOME_PATH) return null

  for (const link of footerLinks || []) {
    const linkPath = resolveFooterLinkPath(link, menus)
    if (linkPath && linkPath === target) return link
  }
  return null
}

function createFooterLinkMenuNode(path, link, config) {
  const type = String(link?.type || '').trim().toUpperCase()
  return {
    id: `footer-link:${type.toLowerCase()}`,
    path,
    name: String(link?.label || '').trim() || config.defaultName,
    templateId: config.templateId,
    contentType: 'INTRODUCTION',
    enabled: true,
    isFooterLink: true
  }
}

/** 页脚配置链接 fallback：路径与页脚地址一致时渲染对应模板 */
function resolveFooterLinkContext(normalizedPath, footerLinks = [], menus = []) {
  const matchedLink = findFooterLinkByPath(normalizedPath, footerLinks, menus)
  if (!matchedLink) return null

  const type = String(matchedLink.type || '').trim().toUpperCase()
  const config = FOOTER_LINK_TYPE_TO_TEMPLATE[type]
  if (!config) return null

  const syntheticMenu = createFooterLinkMenuNode(normalizedPath, matchedLink, config)
  const templateId = resolveTemplateId(config.templateId)

  return {
    path: normalizedPath,
    matchedMenu: syntheticMenu,
    rootMenu: syntheticMenu,
    templateId,
    templateKey: config.templateKey,
    contentVariant: config.contentVariant,
    showHero: true,
    notFound: false,
    isFooterLink: true
  }
}

export { normalizeMenuPath, isPathUnderMenu } from '@/components/portal/common/utils/menuPath'

export function findMenuNodeByPath(menus = [], path) {
  const target = normalizeContentPath(path)
  if (!target || target === HOME_PATH) return null

  let best = null
  let bestLen = -1

  const walk = (nodes = []) => {
    for (const node of nodes) {
      // 兼容根栏目 path 为空、仅子分类有 path：用推导路径匹配 /news/:id 详情
      const nodePath = resolveMenuNodePath(node)
      if (nodePath && nodePath !== HOME_PATH && isPathUnderMenu(target, nodePath)) {
        if (nodePath.length > bestLen) {
          best = node
          bestLen = nodePath.length
        }
      }
      if (node.children?.length) walk(node.children)
    }
  }

  walk(menus)
  return best
}

export function findMenuNodeById(menus = [], menuId) {
  if (menuId == null || menuId === '') return null
  const target = String(menuId)

  const walk = (nodes = []) => {
    for (const node of nodes) {
      if (String(node.id) === target) return node
      const nested = walk(node.children)
      if (nested) return nested
    }
    return null
  }

  return walk(menus)
}

export function findRootMenuNode(menus = [], node) {
  if (!node) return null

  let current = node
  while (current?.parentId != null) {
    const parent = findMenuNodeById(menus, current.parentId)
    if (!parent) break
    current = parent
  }
  return current
}

export function resolveEffectiveTemplateId(node, menus = [], locale) {
  const root = findRootMenuNode(menus, node) || node
  if (!root) return getDefaultTemplateIdForContentType('INTRODUCTION')
  return resolveMenuTemplateId(root, locale)
}

/** 内容式页面子类型（依据菜单树结构，不写死路径） */
export function resolveContentPageVariant(rootMenu) {
  if (!rootMenu) return 'join'

  const visibleChildren = (rootMenu.children || []).filter((item) => isPortalMenuVisible(item))
  if (visibleChildren.length >= 2) return 'join'

  const slug = slugFromMenuPath(rootMenu.path).toLowerCase()
  const name = String(rootMenu.name || '').toLowerCase()

  if (slug.includes('privacy') || name.includes('隐私')) return 'privacy'
  if (slug.includes('legal') || name.includes('法律')) return 'legal'
  return 'join'
}

function resolveTemplateKeyFromMenu(path, menus, matched, locale) {
  const root = findRootMenuNode(menus, matched) || matched
  const templateId = resolveEffectiveTemplateId(matched, menus, locale)
  const templateKey = getTemplateEntry(templateId).key
  const context = {
    path,
    matchedMenu: matched,
    rootMenu: root,
    templateId,
    templateKey,
    contentVariant: null,
    showHero: true,
    notFound: false
  }

  if (templateKey === 'CONTENT_PAGE') {
    context.contentVariant = resolveContentPageVariant(root)
  }

  return context
}

/** 根栏目对应的页脚工具页类型（contact / legal / privacy） */
export function getRootMenuUtilityType(rootMenu, menus = [], locale) {
  if (!rootMenu) return null

  const templateId = resolveEffectiveTemplateId(rootMenu, menus, locale)
  const templateKey = getTemplateEntry(templateId).key

  if (templateKey === 'CONTACT_FORM') return 'contact'
  if (templateKey === 'CONTENT_PAGE') {
    const variant = resolveContentPageVariant(rootMenu)
    if (variant === 'legal' || variant === 'privacy') return variant
  }
  return null
}

/** 按工具页类型查找已启用的 CMS 菜单路径（顶部隐藏但仍启用的栏目可绑定页脚） */
export function findMenuPathByUtilityType(menus = [], utilityType, locale) {
  if (!utilityType) return ''

  const seenRootIds = new Set()
  let foundPath = ''

  const walk = (nodes = []) => {
    for (const node of nodes) {
      const root = findRootMenuNode(menus, node) || node
      const rootId = root?.id
      if (rootId == null || seenRootIds.has(rootId)) {
        if (node.children?.length) walk(node.children)
        continue
      }
      seenRootIds.add(rootId)

      if (root.enabled === false) {
        if (node.children?.length) walk(node.children)
        continue
      }

      if (getRootMenuUtilityType(root, menus, locale) === utilityType) {
        const path = getMenuNodePath(root)
        if (path) {
          foundPath = path
          return true
        }
      }

      if (node.children?.length) walk(node.children)
    }
    return false
  }

  walk(menus)
  return foundPath
}

/** 解析当前路径对应的门户渲染上下文（菜单树优先，页脚配置链接 fallback） */
export function resolvePortalContext(path, menus = [], footerLinks = [], locale) {
  const normalized = normalizeContentPath(path)

  if (normalized === HOME_PATH) {
    return { path: normalized, notFound: true }
  }

  const matched = findMenuNodeByPath(menus, normalized)
  if (matched) {
    return resolveTemplateKeyFromMenu(normalized, menus, matched, locale)
  }

  const footerContext = resolveFooterLinkContext(normalized, footerLinks, menus)
  if (footerContext) {
    return footerContext
  }

  return { path: normalized, notFound: true }
}

/** 当前路径是否为联系表单页（菜单栏目或页脚 CONTACT 链接） */
export function isContactFormRoute(path, menus = [], footerLinks = [], locale) {
  return resolvePortalContext(path, menus, footerLinks, locale).templateKey === 'CONTACT_FORM'
}

const NARROW_PORTAL_TEMPLATES = new Set(['CONTACT_FORM', 'CONTENT_PAGE'])

/** 当前路由是否使用门户宽布局（方案/新闻/关于/产品等） */
export function usesPortalWideLayout(route, menus = [], footerLinks = [], locale) {
  const name = route?.name
  if (name === 'ProductDetail' || name === 'ProductCatalogBackup') return true
  if (name === 'Search' || name === 'Home') return false
  if (name !== 'Portal') return false

  const context = resolvePortalContext(route.path, menus, footerLinks, locale)
  if (context.notFound) return false
  return !NARROW_PORTAL_TEMPLATES.has(context.templateKey)
}

export function resolvePageRootMenu(path, menus = [], footerLinks = []) {
  const matched = findMenuNodeByPath(menus, path)
  if (matched) {
    return findRootMenuNode(menus, matched) || matched
  }

  const normalized = normalizeContentPath(path)
  return resolveFooterLinkContext(normalized, footerLinks, menus)?.rootMenu || null
}
